import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { products } from "../../mock";

const QuoteDialog = ({ open, onOpenChange, presetProduct }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", product: "", quantity: "", grade: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (presetProduct) setForm((f) => ({ ...f, product: presetProduct }));
  }, [presetProduct, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.product) {
      toast({ title: "Please complete required fields", description: "Name, email, phone and product are required." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const stored = JSON.parse(localStorage.getItem("manishlabchem_quotes") || "[]");
      stored.push({ ...form, id: Date.now() });
      localStorage.setItem("manishlabchem_quotes", JSON.stringify(stored));
      setLoading(false);
      setDone(true);
      toast({ title: "Quotation request received", description: "Our sales team will respond within 4 business hours." });
    }, 900);
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", company: "", email: "", phone: "", product: "", quantity: "", grade: "", city: "", message: "" });
  };

  const selectedProduct = products.find((p) => p.name === form.product);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 250); }}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[24px]">Request a Quotation</DialogTitle>
              <DialogDescription>Tell us what you need. Our team responds within 4 business hours with pricing, lead time and CoA samples.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Rakesh Mehta" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company">Company</Label>
                <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Sunrise Pharma Ltd." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Work email *</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="name@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 9XXXXXXXXX" />
              </div>
              <div className="space-y-1.5">
                <Label>Product *</Label>
                <Select value={form.product} onValueChange={(v) => update("product", v)}>
                  <SelectTrigger><SelectValue placeholder="Select a product" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.name}>{p.name} ({p.formula})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Grade</Label>
                <Select value={form.grade} onValueChange={(v) => update("grade", v)} disabled={!selectedProduct}>
                  <SelectTrigger><SelectValue placeholder={selectedProduct ? "Select grade" : "Choose product first"} /></SelectTrigger>
                  <SelectContent>
                    {selectedProduct?.grades.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quantity">Quantity</Label>
                <Input id="quantity" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 5 MT / month" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">Delivery city</Label>
                <Input id="city" value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Ankleshwar, Gujarat" />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="message">Additional requirements</Label>
                <Textarea id="message" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Packaging preference, CoA specs, dispatch schedule…" />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-2">
                <p className="text-[12.5px] text-slate-500">By submitting, you agree to be contacted by our sales team. We never share your data.</p>
                <Button type="submit" disabled={loading} className="bg-teal-700 hover:bg-teal-800 text-white font-semibold h-11 px-6">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send request"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-6 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-teal-700" />
            </div>
            <DialogTitle className="font-display text-[24px] mt-4">Thank you, {form.name.split(" ")[0] || "there"}!</DialogTitle>
            <DialogDescription className="mt-2 max-w-md mx-auto">
              Your request for <strong className="text-slate-900">{form.product}</strong> has been logged. A sales engineer will email you within 4 business hours with pricing, lead time and a sample CoA.
            </DialogDescription>
            <Button onClick={() => onOpenChange(false)} className="mt-6 bg-teal-700 hover:bg-teal-800 text-white px-6 h-10">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
